import { ResourceClient, type HttpClient } from "@dl/web-core-js/api";
import type { OrderDTO } from "./order";

/**
 * The service desk: replacement parts sent to a retailer's customers. SIGM ran
 * it through sales orders in warehouse SERV; there is no pricing here.
 *
 * Shipments are ledger movements pointing at the line, read from the stock
 * ledger filtered by order. Wire names match the Java `@JsonProperty`s.
 */

export const SERVICE_ORDER_STATUSES = ["OPEN", "CLOSED", "CANCELLED"] as const;
export type ServiceOrderStatus = (typeof SERVICE_ORDER_STATUSES)[number];

export const SERVICE_ORDER_KINDS = ["ORDER", "INTERNAL"] as const;
/** ORDER answers a customer's request (linked to the sale when found); INTERNAL has no sale or customer. */
export type ServiceOrderKind = (typeof SERVICE_ORDER_KINDS)[number];

export const SERVICE_INTERNAL_REASONS = ["PLANT_NEED", "SHOWROOM", "SERVICE_STOCK", "OTHER"] as const;
export type ServiceInternalReason = (typeof SERVICE_INTERNAL_REASONS)[number];

export interface ServiceMotiveDTO {
  id: number;
  code: string;
  name: string;
  is_active: boolean;
}

export interface ServiceOrderDTO {
  id: number;
  /** Assigned by the database; nexpim numbers from 100001, imported orders keep SIGM's. */
  number: number;
  /** Changed only through the order's actions and by shipping it in full. */
  status: ServiceOrderStatus;
  /** The Nexera client of the linked sale; null = No client. Set by the server from the sale. */
  client_id: number | null;
  client_number?: string | null;
  client_name?: string | null;
  warehouse_id: number;
  warehouse_code?: string | null;
  order_date: string | null;
  /** The retailer's order number, as typed. */
  client_order_reference: string | null;
  /** The Nexera sale, when the reference resolved to one. */
  nexera_order_number: number | null;
  ship_name: string | null;
  ship_name_2: string | null;
  ship_address: string | null;
  ship_city: string | null;
  ship_postal_code: string | null;
  ship_phone: string | null;
  ship_email: string | null;
  note: string | null;
  created_by_account_id?: number | null;
  sigm_agent?: string | null;
  closed_at?: string | null;
  sigm_order_id?: number | null;
  line_count?: number;
  open_line_count?: number;
  last_shipment_date?: string | null;
  /** The SKUs on the order, comma-separated. */
  products?: string | null;
  kind: ServiceOrderKind;
  /** INTERNAL orders only. */
  internal_reason?: ServiceInternalReason | null;
  /** INTERNAL orders only: who asked, as typed. */
  requested_by?: string | null;
}

/** Whether a part can ship from the service shelf rather than be made. */
export interface ServicePartAvailabilityDTO {
  part_id: number;
  warehouse_code: string | null;
  on_hand: number;
  /** Still to ship on other open service orders from the shelf. */
  promised: number;
  /** On hand less promised, never below zero. */
  available: number;
  /** At the plant's primary warehouse: a transfer, not a production order. */
  plant_on_hand: number;
}

export interface ServiceOrderLineDTO {
  id: number;
  service_order_id: number;
  position: number;
  part_id: number;
  part_number?: string | null;
  part_name?: string | null;
  stock_unit_code?: string | null;
  quantity: number;
  quantity_cancelled: number;
  /** Computed: SIGM before cutover plus the ledger. */
  quantity_shipped?: number | null;
  quantity_outstanding?: number | null;
  service_motive_id: number | null;
  service_motive_code?: string | null;
  service_motive_name?: string | null;
  product_id?: number | null;
  /** The product the customer owns, as typed; resolved to the catalogue server-side. */
  product_sku: string | null;
  production_order_id?: number | null;
  production_order_number?: number | null;
  /** The run number off the box label; resolved to a production order server-side. */
  production_reference: string | null;
  is_made_to_order: boolean;
  requested_from_plant_date: string | null;
  made_date: string | null;
  ready_date: string | null;
  note: string | null;
  /**
   * The SERVICE lot component making this line's part, when it was sent to the
   * plant. Not production_order_id, which is the run that built the customer's
   * faulty unit.
   */
  making_component_id?: number | null;
  making_component_code?: string | null;
  making_lot_id?: number | null;
  making_lot_number?: number | null;
  making_lot_status?: string | null;
  making_quantity?: number | null;
  /** 0 to 1. */
  making_progress?: number | null;
}

export interface ServiceShipmentRequest {
  document_date: string | null;
  /** The carrier's tracking number. */
  reference: string | null;
  note: string | null;
  lines: Array<{ service_order_line_id: number; quantity: number }>;
}

export interface ServiceShipmentResult {
  stock_document_id: number;
  /** Nothing was left to send, so the order closed itself. */
  closed: boolean;
  lines: Array<{
    part_number: string;
    quantity: number;
    outstanding_after: number;
    over_shipped: boolean;
    stock_movement_id: number;
  }>;
}

/** A component of a sold product, one per BOM occurrence. */
export interface ServiceSaleComponentDTO {
  level: number;
  path: string;
  part_id: number;
  part_number: string;
  part_name: string;
  /** Per one product. */
  quantity: number;
  unit_code: string | null;
  /** A kind of part the service desk sends (panel, hardware, back…), not packaging or raw material. */
  usual: boolean;
}

/** A product on the Nexera order a service order answers, with its bill of materials. */
export interface ServiceSaleProductDTO {
  order_number: number;
  sku: string;
  description: string | null;
  quantity: number;
  product_id: number | null;
  part_id: number | null;
  part_number: string | null;
  components: ServiceSaleComponentDTO[];
}

export class ServiceOrderClient extends ResourceClient<ServiceOrderDTO> {
  constructor(http: HttpClient) {
    super(http, "/servicedesk/service-order");
  }

  private action(id: number, action: "close" | "cancel" | "reopen"): Promise<ServiceOrderDTO> {
    return this.http.post<ServiceOrderDTO>(`${this.path}/${String(id)}/${action}`, {});
  }

  close(id: number): Promise<ServiceOrderDTO> {
    return this.action(id, "close");
  }

  cancel(id: number): Promise<ServiceOrderDTO> {
    return this.action(id, "cancel");
  }

  reopen(id: number): Promise<ServiceOrderDTO> {
    return this.action(id, "reopen");
  }

  /** The client an order linked to this Nexera order would get on save: the sale's, traced back from a replacement order, or none. */
  async saleClient(orderNumber: number): Promise<{ client_id: number; client_number: string; client_name: string } | null> {
    const found = await this.http.get<{ client_id?: number; client_number?: string; client_name?: string }>(`${this.path}/sale-client`, {
      query: { order: String(orderNumber) },
    });
    return found.client_id == null ? null : { client_id: found.client_id, client_number: found.client_number ?? "", client_name: found.client_name ?? "" };
  }

  /** The products of the linked sale (and the sale behind a replacement order), with their parts. */
  saleParts(id: number): Promise<ServiceSaleProductDTO[]> {
    return this.http.get<ServiceSaleProductDTO[]>(`${this.path}/${String(id)}/sale-parts`);
  }

  /** The same for a Nexera sale before any service order exists: what the new-order dialog lists. */
  salePartsOfSale(orderNumber: number): Promise<ServiceSaleProductDTO[]> {
    return this.http.get<ServiceSaleProductDTO[]>(`${this.path}/sale-parts`, { query: { order: String(orderNumber) } });
  }

  /** Service-shelf availability for these parts; pass the order being edited so its own lines do not hide the stock. */
  availability(partIds: number[], excludeOrderId?: number): Promise<ServicePartAvailabilityDTO[]> {
    if (partIds.length === 0) return Promise.resolve([]);
    return this.http.get<ServicePartAvailabilityDTO[]>(`${this.path}/availability`, {
      query: { part_id: partIds, exclude_order: excludeOrderId },
    });
  }

  /** An order and its first lines, all or nothing. */
  createWithLines(order: ServiceOrderDTO, lines: ServiceOrderLineDTO[]): Promise<ServiceOrderDTO> {
    return this.http.post<ServiceOrderDTO>(`${this.path}/with-lines`, { order, lines });
  }

  ship(id: number, request: ServiceShipmentRequest): Promise<ServiceShipmentResult> {
    return this.http.post<ServiceShipmentResult>(`${this.path}/${String(id)}/ship`, request);
  }
}

export class ServiceOrderLineClient extends ResourceClient<ServiceOrderLineDTO> {
  constructor(http: HttpClient) {
    super(http, "/servicedesk/service-order-line");
  }
}

export class ServiceMotiveClient extends ResourceClient<ServiceMotiveDTO> {
  constructor(http: HttpClient) {
    super(http, "/servicedesk/service-motive");
  }
}

/**
 * Nexera's sales orders, searched for the one a service request answers. Each
 * search scans SIGM's copy, so it asks for one short page.
 */
export class NexeraOrderSearch {
  private readonly orders: ResourceClient<OrderDTO>;

  constructor(http: HttpClient) {
    this.orders = new ResourceClient<OrderDTO>(http, "/reporting/order");
  }

  async search(term: string): Promise<OrderDTO[]> {
    const trimmed = term.trim();
    if (trimmed === "") return [];
    // A Wayfair replacement reference carries a suffix the sale never had.
    const reference = trimmed.replace(/-R-\d+$/i, "");
    const filters: Record<string, string[]> = /^\d{5,7}$/.test(reference)
      ? { number: [reference] }
      : { client_order_no: [reference] };
    const page = await this.orders.find({ page: 0, size: 10, filters, sort: ["order_date,desc"] });
    if (page.content.length > 0) return page.content;
    // Nothing on the reference: try it as the customer's name.
    return (await this.orders.find({ page: 0, size: 10, filters: { delivery_name: [trimmed] }, sort: ["order_date,desc"] })).content;
  }
}
